import { StanColumnDef } from '@/components/core-components/StanTable/StanTable.types';
import { PaymentOrder } from '@/lib/api/income/types';
import { ProductPayment } from '../types/transactionTypes';
import { useOrders } from '../hooks/useOrders';
import { StanTable } from '@/components/core-components/StanTable/StanTable';
import { PAYMENT_STATUS, PAYMENT_TYPES, STAN_TAG_PRESETS } from '@/lib/constants';
import { StanTag } from '@/components/core-components/StanTag/StanTag';
import { formatCurrency, formatDateShort } from '@/lib';
import { cn } from '@/lib/utils/cn';

import { useStanRouter } from '@/hooks/useStanRouter';
import { StanButton, StanCard, StanFilters, StanTooltip } from '@/components';
import IcDownload from '@/assets/icons/ic-download.svg';
import IcArrowRight from '@/assets/icons/ic-arrow-right.svg';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import {
    getOrderPaymentInfo,
    getOrderStatus,
    getPaymentPlanLabel,
    getPayoutStatusLabel,
    isAffiliateTransaction,
    isOrderBump,
    isOrderNeedsFulfillment,
    isPaymentPlan,
    isPayoutOrder,
} from '../lib/orders.logic';
import { useOrderFilters } from '../hooks/useOrderFilters';
import { useQueryFilters } from '@/hooks/useQueryFilters';
import { useModal } from '@/hooks/useModal';
import { FulfillOrderModal } from './FulfillOrderModal';
import { ExportOrdersModal } from './ExportOrdersModal';

// Helper function to create order columns with fulfillment support
const createOrderColumns = (
    onFulfillOrder: (order: PaymentOrder) => void,
    hasOrdersToFulfill: boolean
): StanColumnDef<PaymentOrder>[] => {
    const columns: StanColumnDef<PaymentOrder>[] = [
        {
            header: 'Date',
            accessorKey: 'created_at',
            width: '15%',
            cell: ({ row }) => {
                const order = row.original;
                return <span>{formatDateShort(order.created_at)}</span>;
            },
        },
        {
            header: 'Email',
            accessorKey: 'email',
            width: '25%',
            hideOnMobile: true,
        },
        {
            header: 'Product',
            accessorKey: 'data',
            width: '45%',
            cell: ({ row }) => {
                const order = row.original;
                const title = order.data.title;
                const productType = order.product_type;
                const productTagBGColor = STAN_TAG_PRESETS?.['product']?.[productType]?.backgroundColor;
                const productTagTextColor = STAN_TAG_PRESETS?.['product']?.[productType]?.textColor;
                const productTagLabel = STAN_TAG_PRESETS?.['product']?.[productType]?.label ?? 'Product';

                const disputeStatus = order.data?.dispute_status;

                const productTagStyle =
                    productTagBGColor && productTagTextColor
                        ? { backgroundColor: productTagBGColor, color: productTagTextColor }
                        : undefined;
                const disputeTagStyle =
                    disputeStatus &&
                    STAN_TAG_PRESETS?.['disputeStatus']?.[disputeStatus]?.backgroundColor &&
                    STAN_TAG_PRESETS?.['disputeStatus']?.[disputeStatus]?.textColor
                        ? {
                              backgroundColor: STAN_TAG_PRESETS['disputeStatus'][disputeStatus].backgroundColor,
                              color: STAN_TAG_PRESETS['disputeStatus'][disputeStatus].textColor,
                          }
                        : undefined;

                return (
                    <div className='flex flex-wrap items-center gap-2'>
                        {productType !== PAYMENT_TYPES.PAYOUT && (
                            <StanTag label={productTagLabel} style={productTagStyle} />
                        )}
                        {disputeStatus && (
                            <StanTag
                                label={STAN_TAG_PRESETS?.['disputeStatus']?.[disputeStatus]?.label}
                                style={disputeTagStyle}
                            />
                        )}
                        {isPaymentPlan(order) && (
                            <StanTag
                                label={getPaymentPlanLabel(order)}
                                style={{
                                    backgroundColor: 'var(--stan-gray-primary-color)',
                                    color: 'var(--stan-text-dark-color)',
                                }}
                            />
                        )}
                        {isPayoutOrder(order) && (
                            <>
                                {order.status === PAYMENT_STATUS.CREATOR_FULFILLED && (
                                    <StanTag
                                        label='Paid'
                                        style={{
                                            backgroundColor: STAN_TAG_PRESETS?.invoiceStatus?.paid?.backgroundColor,
                                            color: STAN_TAG_PRESETS?.invoiceStatus?.paid?.textColor,
                                        }}
                                    />
                                )}
                                {order.status === PAYMENT_STATUS.FAILED && (
                                    <StanTag
                                        label='Failed'
                                        style={{
                                            backgroundColor: 'var(--stan-negative-light-color)',
                                            color: 'var(--stan-text-negative-color)',
                                        }}
                                    />
                                )}
                                {order.status === PAYMENT_STATUS.PENDING && (
                                    <StanTag
                                        label={getPayoutStatusLabel(order)}
                                        style={{
                                            backgroundColor: 'var(--stan-gray-primary-color)',
                                            color: 'var(--stan-text-dark-color)',
                                        }}
                                    />
                                )}
                            </>
                        )}
                        {isAffiliateTransaction(order) && (
                            <StanTag
                                label={`via @${order.affiliated_username || ''}`}
                                style={{
                                    backgroundColor: 'var(--stan-gray-primary-color)',
                                    color: 'var(--stan-text-dark-color)',
                                }}
                            />
                        )}
                        {isOrderBump(order) && (
                            <StanTag
                                label='Order Bump'
                                style={{
                                    backgroundColor: 'var(--stan-gray-primary-color)',
                                    color: 'var(--stan-text-dark-color)',
                                }}
                            />
                        )}
                        <span className='truncate'>{title}</span>
                    </div>
                );
            },
        },
        {
            header: 'Amount',
            accessorKey: 'amount',
            width: '15%',
            cell: ({ row }) => {
                const order = row.original;
                const colorClassName = getOrderStatus(order);
                const paymentInfo = getOrderPaymentInfo(order);

                return (
                    <StanTooltip content={paymentInfo} position='bottom' trigger='hover'>
                        <div className='text-caption-1-bold flex items-center gap-2'>
                            <span className={cn(colorClassName)}>
                                {formatCurrency(order.amount, order.pos_currency)}
                            </span>
                        </div>
                    </StanTooltip>
                );
            },
        },
    ];

    // Add fulfillment column if there are orders to fulfill
    if (hasOrdersToFulfill) {
        columns.push({
            header: '',
            accessorKey: 'fulfill',
            width: '150px',
            cell: ({ row }) => {
                const order = row.original;
                if (isOrderNeedsFulfillment(order)) {
                    return (
                        <div className='flex justify-end'>
                            <StanButton
                                name='orders-fufill-button'
                                color='secondary'
                                size='xs'
                                icon={IcArrowRight}
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    e.stopPropagation();
                                    onFulfillOrder(order);
                                }}
                            >
                                Fulfill Order
                            </StanButton>
                        </div>
                    );
                }
                return null;
            },
        });
    }

    return columns;
};

interface IncomeOrdersProps {
    userFanId?: number;
    customerName?: string;
}

export interface IncomeOrdersRef {
    refetch: () => void;
}

export const IncomeOrders = forwardRef<IncomeOrdersRef, IncomeOrdersProps>(({ userFanId, customerName }, ref) => {
    const filters = useOrderFilters(userFanId);
    const { filterValues, backendFilterValues, handleFilterChange, handleResetFilters } = useQueryFilters({ filters });

    const {
        items,
        isFetching,
        currentPage,
        totalPages,
        goToPage,
        refetch: refetchOrders,
    } = useOrders({
        per_page: 15,
        filter_array: backendFilterValues,
        ...(userFanId && { user_fan_id: userFanId }),
    });

    // Expose refetch method via ref
    useImperativeHandle(
        ref,
        () =>
            ({
                refetch: refetchOrders,
            }) as IncomeOrdersRef
    );

    const { push } = useStanRouter();
    const { isOpen: isExportModalOpen, open: openExportModal, close: closeExportModal } = useModal();
    const { isOpen: isFulfillModalOpen, open: openFulfillModal, close: closeFulfillModal } = useModal();
    const [selectedOrder, setSelectedOrder] = useState<PaymentOrder | null>(null);

    // Check if any order on current page needs fulfillment
    const hasOrdersToFulfill = useMemo(() => {
        return items.some(order => isOrderNeedsFulfillment(order));
    }, [items]);

    const handleFulfillOrder = useCallback(
        (order: PaymentOrder) => {
            setSelectedOrder(order);
            openFulfillModal();
        },
        [openFulfillModal]
    );

    const handleOrderFulfilled = () => {
        refetchOrders();
        closeFulfillModal();
        setSelectedOrder(null);
    };

    const orderColumns = useMemo(
        () => createOrderColumns(handleFulfillOrder, hasOrdersToFulfill),
        [hasOrdersToFulfill, handleFulfillOrder]
    );

    return (
        <StanCard>
            <div className='mb-5 w-full'>
                <div className='mb-5 flex items-start justify-between'>
                    <h3>Latest Orders</h3>
                    <StanButton
                        name='orders-export-csv-button'
                        color='secondary'
                        size='xs'
                        icon={IcDownload}
                        onClick={openExportModal}
                        className='!-mt-2 w-fit'
                        disabled={items.length === 0}
                    >
                        Download CSV
                    </StanButton>
                </div>

                <StanFilters
                    onFilterChanged={handleFilterChange}
                    onResetFilters={handleResetFilters}
                    filters={filters}
                    filterValues={filterValues}
                />
            </div>
            <StanTable
                data={items}
                columns={orderColumns}
                onPageChange={goToPage}
                page={currentPage}
                totalPages={totalPages}
                isLoading={isFetching}
                onRowClick={row => {
                    // If viewing from customer context, pass customer info for breadcrumbs
                    const params = new URLSearchParams();
                    if (userFanId) params.set('userFanId', userFanId.toString());
                    if (customerName) params.set('customerName', customerName);
                    const queryString = params.toString();
                    push(`/income/details/${row.id}${queryString ? `?${queryString}` : ''}`);
                }}
                emptyHeader='No Transactions Matching Filters'
                emptySubheader="Update filters to find what you're looking for!"
            />
            <ExportOrdersModal
                isOpen={isExportModalOpen}
                onClose={closeExportModal}
                backendFilterValues={backendFilterValues}
                userFanId={userFanId}
            />
            {selectedOrder && (
                <FulfillOrderModal
                    isOpen={isFulfillModalOpen}
                    onClose={closeFulfillModal}
                    transactionData={selectedOrder as unknown as ProductPayment}
                    orderId={selectedOrder.id}
                    onOrderFulfilled={handleOrderFulfilled}
                />
            )}
        </StanCard>
    );
});

IncomeOrders.displayName = 'IncomeOrders';
